import React from 'react';
import './titration.css';

interface BuretteProps {
  readingCm3: number;
  x?: number;
  y?: number;
  height?: number;
}

export const Burette: React.FC<BuretteProps> = ({ readingCm3, x = 420, y = 45, height = 310 }) => {
  const safeReading = Math.max(0, Math.min(50, readingCm3));
  const meniscusY = y + (safeReading / 50) * height;
  const graduations = Array.from({ length: 501 }, (_, index) => {
    const value = index / 10;
    const tickY = y + (value / 50) * height;
    const isMajor = index % 10 === 0;
    const isLabel = index % 50 === 0;
    return (
      <g key={index} className={isMajor ? 'burette-graduation-major' : 'burette-graduation-minor'}>
        <line x1={x + 29} x2={x + (isMajor ? 43 : 35)} y1={tickY} y2={tickY} />
        {isLabel && <text x={x + 48} y={tickY + 4}>{value.toFixed(0)}</text>}
      </g>
    );
  });

  return (
    <g aria-label="50 cubic centimetre burette, zero at the top">
      <rect className="glass-outline" x={x + 12} y={y} width="19" height={height} rx="8" />
      <path className="burette-liquid" d={`M${x + 14} ${meniscusY} Q${x + 21.5} ${meniscusY + 4} ${x + 29} ${meniscusY} L${x + 29} ${y + height - 3} L${x + 14} ${y + height - 3} Z`} />
      <path className="meniscus-line" d={`M${x + 14} ${meniscusY} Q${x + 21.5} ${meniscusY + 4} ${x + 29} ${meniscusY}`} />
      <g aria-label="Burette graduations, 0.1 cm3 intervals, 501 marks">{graduations}</g>
      <path className="glass-tip" d={`M${x + 16} ${y + height} L${x + 21.5} ${y + height + 23} L${x + 27} ${y + height}`} />
      <rect className="stopcock-body" x={x + 6} y={y + height + 24} width="31" height="8" rx="4" />
      <line className="stopcock-handle" x1={x + 21.5} y1={y + height + 20} x2={x + 21.5} y2={y + height + 37} />
      <line className="glass-tip" x1={x + 21.5} y1={y + height + 32} x2={x + 21.5} y2={y + height + 48} />
    </g>
  );
};

export const RetortStand: React.FC<{ x?: number; y?: number }> = ({ x = 382, y = 34 }) => (
  <g aria-label="Retort stand">
    <rect className="stand-base" x={x - 55} y={y + 425} width="110" height="10" rx="4" />
    <rect className="stand-rod" x={x - 3} y={y} width="6" height="426" rx="3" />
    <circle className="stand-foot" cx={x} cy={y + 427} r="6" />
  </g>
);

export const BuretteClamp: React.FC<{ x?: number; y?: number }> = ({ x = 388, y = 103 }) => (
  <g aria-label="Burette clamp">
    <path className="clamp-metal" d={`M${x} ${y} h35 v8 h-23 v26 h-12 z`} />
    <circle className="clamp-knob" cx={x + 36} cy={y + 5} r="5" />
  </g>
);

export const ConicalFlask: React.FC<{ colour: string; x?: number; y?: number; swirling?: boolean }> = ({
  colour,
  x = 362,
  y = 398,
  swirling = false,
}) => (
  <g className={swirling ? 'flask-swirling' : ''} aria-label="250 cubic centimetre conical flask">
    <path className="flask-glass" d={`M${x + 42} ${y} h42 v25 l40 84 q5 12 -9 16 h-104 q-14 -4 -9 -16 l40 -84 z`} />
    <path className="flask-solution" aria-label={`Flask solution, ${colour}`} style={{ fill: colour }} d={`M${x + 22} ${y + 107} h82 l18 35 q5 12 -9 16 h-104 q-14 -4 -9 -16 z`} />
    <path className="flask-rim" d={`M${x + 39} ${y} h48 M${x + 42} ${y + 4} h42`} />
    <path className="flask-highlight" d={`M${x + 48} ${y + 31} l-34 75 M${x + 76} ${y + 34} l34 71`} />
  </g>
);

export const WhiteTile: React.FC<{ x?: number; y?: number }> = ({ x = 350, y = 555 }) => (
  <g aria-label="White endpoint-observation tile">
    <ellipse className="white-tile" cx={x + 95} cy={y} rx="118" ry="13" />
    <ellipse className="tile-highlight" cx={x + 95} cy={y - 2} rx="107" ry="8" />
  </g>
);

export const Pipette: React.FC<{ x?: number; y?: number }> = ({ x = 108, y = 122 }) => (
  <g aria-label="25 cubic centimetre volumetric pipette">
    <path className="pipette-glass" d={`M${x + 16} ${y} h12 v94 q0 14 8 26 l-5 130 h-18 l-5 -130 q8 -12 8 -26 z`} />
    <line className="pipette-mark" x1={x + 12} y1={y + 103} x2={x + 32} y2={y + 103} />
    <text className="apparatus-label" x={x - 7} y={y + 274}>25 cm³ pipette</text>
  </g>
);

export const PipetteFiller: React.FC<{ x?: number; y?: number }> = ({ x = 105, y = 75 }) => (
  <g aria-label="Pipette filler">
    <path className="pipette-filler" d={`M${x + 8} ${y + 28} C${x - 8} ${y + 6} ${x + 7} ${y - 7} ${x + 22} ${y + 10} C${x + 37} ${y - 7} ${x + 52} ${y + 6} ${x + 36} ${y + 28} L${x + 30} ${y + 42} h-16 z`} />
    <circle className="filler-valve" cx={x + 22} cy={y + 4} r="4" />
  </g>
);

export const VolumetricFlask: React.FC<{ x?: number; y?: number }> = ({ x = 160, y = 374 }) => (
  <g aria-label="250 cubic centimetre volumetric flask">
    <path className="volumetric-glass" d={`M${x + 26} ${y} h26 v49 l28 43 q8 14 -8 20 h-66 q-16 -6 -8 -20 l28 -43 z`} />
    <path className="flask-rim" d={`M${x + 24} ${y + 11} h30`} />
    <line className="pipette-mark" x1={x + 22} y1={y + 63} x2={x + 56} y2={y + 63} />
    <text className="apparatus-label" x={x - 5} y={y + 130}>Volumetric flask</text>
  </g>
);

export const AnalyticalBalance: React.FC<{ x?: number; y?: number }> = ({ x = 38, y = 523 }) => (
  <g aria-label="Analytical balance, 0.0001 gram readability">
    <rect className="balance-body" x={x} y={y} width="125" height="40" rx="7" />
    <rect className="balance-display" x={x + 60} y={y + 10} width="52" height="17" rx="3" />
    <text className="balance-text" x={x + 66} y={y + 22}>0.0000 g</text>
    <path className="balance-pan" d={`M${x + 10} ${y + 7} h38 l-5 8 h-28 z`} />
  </g>
);

export const WashBottle: React.FC<{ x?: number; y?: number }> = ({ x = 735, y = 421 }) => (
  <g aria-label="Distilled water wash bottle">
    <path className="bottle-body" d={`M${x + 10} ${y + 24} h42 v58 q0 9 -9 9 h-24 q-9 0 -9 -9 z`} />
    <path className="bottle-neck" d={`M${x + 21} ${y + 23} v-12 h19 v13 M${x + 39} ${y + 13} l22 -14`} />
    <text className="apparatus-label" x={x - 3} y={y + 108}>Wash bottle</text>
  </g>
);

export const DroppingBottle: React.FC<{ x?: number; y?: number }> = ({ x = 820, y = 426 }) => (
  <g aria-label="Indicator dropping bottle">
    <rect className="reagent-bottle" x={x + 8} y={y + 18} width="40" height="61" rx="8" />
    <rect className="bottle-neck" x={x + 19} y={y + 8} width="18" height="13" rx="3" />
    <rect className="dropper-cap" x={x + 21} y={y} width="14" height="10" rx="4" />
    <text className="apparatus-label" x={x - 2} y={y + 101}>Indicator</text>
  </g>
);

export const Stopwatch: React.FC<{ seconds?: number; x?: number; y?: number }> = ({ seconds = 0, x = 824, y = 108 }) => (
  <g aria-label={`Stopwatch, ${seconds.toFixed(1)} seconds`}>
    <circle className="stopwatch-face" cx={x} cy={y} r="33" />
    <path className="stopwatch-top" d={`M${x - 6} ${y - 38} h12 v6 h-12 z M${x + 15} ${y - 35} l8 -7 5 5 -8 8`} />
    <text className="stopwatch-text" x={x - 20} y={y + 5}>{seconds.toFixed(1)}s</text>
  </g>
);

export const MeniscusView: React.FC<{ readingCm3: number }> = ({ readingCm3 }) => (
  <g aria-label={`Meniscus close-up, burette reading ${readingCm3.toFixed(2)} cubic centimetres`}>
    <circle className="meniscus-lens" cx="744" cy="205" r="58" />
    <clipPath id="meniscus-lens-clip"><circle cx="744" cy="205" r="54" /></clipPath>
    <g clipPath="url(#meniscus-lens-clip)">
      <rect className="lens-glass" x="700" y="151" width="88" height="108" />
      <rect className="lens-liquid" x="704" y="207" width="80" height="55" />
      <path className="meniscus-line" d="M704 204 Q744 213 784 204" />
      <line className="lens-graduation" x1="770" y1="195" x2="784" y2="195" />
      <line className="lens-graduation" x1="775" y1="218" x2="784" y2="218" />
      <text className="lens-reading" x="708" y="239">{readingCm3.toFixed(2)} cm³</text>
    </g>
    <text className="apparatus-label" x="707" y="276">Read bottom of meniscus</text>
  </g>
);

export const DropFormation: React.FC<{ active: boolean; x?: number; y?: number }> = ({ active, x = 429, y = 397 }) => (
  <g aria-hidden="true" className={active ? 'drop-active' : ''}>
    <path className="titrant-drop" d={`M${x} ${y - 13} C${x - 9} ${y} ${x - 8} ${y + 7} ${x} ${y + 8} C${x + 8} ${y + 7} ${x + 9} ${y} ${x} ${y - 13} Z`} />
  </g>
);

export const SwirlAnimation: React.FC<{ active: boolean; x?: number; y?: number }> = ({ active, x = 427, y = 477 }) => (
  <g aria-hidden="true" className={active ? 'swirl-active' : ''}>
    <ellipse className="swirl-ring" cx={x} cy={y} rx="30" ry="9" />
  </g>
);

export const ColourChangeEffect: React.FC<{ colour: string }> = ({ colour }) => (
  <span className="colour-change-swatch" style={{ backgroundColor: colour }} aria-label={`Current flask colour: ${colour}`} />
);

interface TitrationStationProps {
  buretteReadingCm3: number;
  flaskColour: string;
  stopwatchSeconds?: number;
  dropActive?: boolean;
  swirling?: boolean;
}

const TitrationStation: React.FC<TitrationStationProps> = ({
  buretteReadingCm3,
  flaskColour,
  stopwatchSeconds = 0,
  dropActive = false,
  swirling = false,
}) => (
  <div className="titration-station">
    <svg viewBox="0 0 940 625" role="img" aria-labelledby="titration-station-title titration-station-description">
      <title id="titration-station-title">Virtual volumetric analysis station</title>
      <desc id="titration-station-description">A graduated burette clamped above a conical flask on a white tile, with pipette, volumetric flask, balance, wash bottle, indicator, meniscus zoom, and stopwatch.</desc>
      <defs>
        <linearGradient id="glassGradient" x1="0" x2="1">
          <stop offset="0" stopColor="#dff5ff" stopOpacity="0.38" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.78" />
          <stop offset="1" stopColor="#b9e8f8" stopOpacity="0.35" />
        </linearGradient>
        <linearGradient id="benchGradient" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#e5edf0" />
          <stop offset="1" stopColor="#cfdadd" />
        </linearGradient>
      </defs>
      <rect className="lab-wall" x="0" y="0" width="940" height="625" rx="12" />
      <path className="lab-bench" d="M0 566 H940 V625 H0 Z" />
      <RetortStand />
      <BuretteClamp />
      <Burette readingCm3={buretteReadingCm3} />
      <DropFormation active={dropActive} />
      <WhiteTile />
      <ConicalFlask colour={flaskColour} swirling={swirling} />
      <SwirlAnimation active={swirling} />
      <PipetteFiller />
      <Pipette />
      <VolumetricFlask />
      <AnalyticalBalance />
      <WashBottle />
      <DroppingBottle />
      <Stopwatch seconds={stopwatchSeconds} />
      <MeniscusView readingCm3={buretteReadingCm3} />
      <text className="station-caption" x="25" y="30">TITRATION STATION · 25.00 cm³ ALIQUOT · 50 cm³ BURETTE</text>
    </svg>
    <div className="station-legend">
      <span><i className="legend-glass" />Glassware</span>
      <span><i className="legend-titrant" />Titrant</span>
      <span><i className="legend-analyte" />Analyte + indicator</span>
      <span><ColourChangeEffect colour={flaskColour} />Flask state</span>
    </div>
  </div>
);

export default TitrationStation;